/**
 * NavigationRail — 화면 우측 세로 모드 전환 바
 * 아이콘 + 라벨 버튼을 세로로 쌓는다. 항목별 숫자 배지·비활성·툴팁 지원.
 */

import { BetaFeatureOnly } from "@/components/beta-feature";
import { cn } from "@/lib/utils";
import { NUMBER_BADGE_COUNT_MAX, NumberBadge } from "@/toggle-ui-kit/badge";
import { Tooltip } from "@/toggle-ui-kit/tooltip";
import { Fragment, type ComponentType } from "react";

export type NavigationRailItem<Id extends string> = {
  id: Id;
  label: string;
  Icon: ComponentType<{ className?: string }>;
  /** 0 이하면 배지를 그리지 않는다 */
  count?: number;
  disabled?: boolean;
  /** 마우스를 올리면 왼쪽에 뜨는 안내. 주로 비활성 사유 */
  tooltip?: string;
  /** 베타에서만 보인다 (베타 인스펙터 마크 포함). 기본 false */
  betaOnly?: boolean;
};

export const NavigationRail = <Id extends string>({
  ariaLabel,
  items,
  activeId,
  emphasizeActive = false,
  onSelect,
}: {
  ariaLabel: string;
  items: NavigationRailItem<Id>[];
  activeId: Id;
  /** 현재 항목에 테두리·halo 를 둘러 클릭을 유도한다. false 면 배경만 */
  emphasizeActive?: boolean;
  onSelect: (id: Id) => void;
}) => (
  <nav
    aria-label={ariaLabel}
    className="flex w-[92px] shrink-0 flex-col items-center gap-1 border-l border-line-normal bg-background-base px-2 py-3"
  >
    {items.map((item) => {
      const { id, label, Icon, count = 0, disabled, tooltip } = item;
      const active = id === activeId;
      const button = (
        <button
          type="button"
          aria-current={active ? "page" : undefined}
          disabled={disabled}
          onClick={() => onSelect(id)}
          className={cn(
            "flex w-full flex-col items-center gap-1 rounded-[10px] px-1 py-2 caption-2-medium transition-colors disabled:cursor-not-allowed disabled:text-label-assistive",
            active
              ? emphasizeActive
                ? // 흰 배경 + 파란 테두리 2px + 연한 파란 halo 4px (항목 간격 4px 안)
                  "bg-background-base text-primary-color ring-4 ring-blue-500/36 ring-offset-2 ring-offset-primary-color"
                : "bg-blue-500/8 text-primary-color"
              : "text-label-alternative hover:bg-decorate-neutral/6 hover:text-label-normal",
          )}
        >
          <span className="relative">
            <Icon className="size-6" />
            {count > 0 && (
              <NumberBadge
                max={NUMBER_BADGE_COUNT_MAX}
                className="absolute left-3.5 top-2 bg-status-negative text-static-white"
              >
                {count}
              </NumberBadge>
            )}
          </span>
          <span className="whitespace-nowrap">{label}</span>
        </button>
      );

      const node = tooltip ? (
        <Tooltip label={tooltip} side="left" align="center">
          <span className="flex w-full">{button}</span>
        </Tooltip>
      ) : (
        <span className="flex w-full">{button}</span>
      );
      return item.betaOnly ? (
        <BetaFeatureOnly key={id}>{node}</BetaFeatureOnly>
      ) : (
        <Fragment key={id}>{node}</Fragment>
      );
    })}
  </nav>
);
